import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { StepperComponent } from './stepper/stepper.component';
import { AuthContextService } from 'src/app/services/auth-context.service';
import { AuthService } from 'src/app/services/auth.service';
import {
  AbstractControl,
  FormControl,
  FormControlStatus,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ButtonComponent } from '../buttons/button/button.component';
import { CurrencyPipe } from '@angular/common';
import { BraintreePaymentComponent } from '../braintree-payment/braintree-payment.component';
import { CartInfo, CheckoutService } from 'src/app/course/services/checkout.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, Subscription, take } from 'rxjs';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmationModalComponent } from '../confirmation/confirmation-modal.component';
import { IconButtonComponent } from '../buttons/icon-button/icon-button.component';
import { SignUpFormComponent } from '../forms/sign-up-form/sign-up-form.component';
import { SignInFormComponent } from '../forms/sign-in-form/sign-in-form.component';
import { ContactInfoFormComponent } from '../forms/contact-info-form/contact-info-form.component';
import { BillingInfoFormComponent } from '../forms/billing-info-form/billing-info-form.component';
import { ForgotPasswordFormComponent } from '../forms/forgot-password-form/forgot-password-form.component';
import { MagicLinkFormComponent } from '../forms/magic-link-form/magic-link-form.component';
import { IntStates } from 'src/app/course/tests/enum/braintree-countries.enum';
import { Router } from '@angular/router';
import { CourseService } from 'src/app/course/services/course.service';

export interface CheckoutModalDialogData {
  courseId: string;
  courseName: string;
  courseWebPurchaseUrl: string;
  courseShoppingCartImagelUrl: string;
  error: boolean;
  price: string;
}

interface PricesResponse {
  baseDiscountAmount: number;
  baseGrandTotal: number;
  baseSubtotal: number;
  baseSubtotalInclTax: number;
  baseSubtotalWithDiscount: number;
  baseTaxAmount: number;
  discountAmount: number;
  grandTotal: number;
  subtotal: number;
  subtotalInclTax: number;
  subtotalWithDiscount: number;
  taxAmount: number;
}

const matchEmails: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const email = control.get('email');
  const confirmEmail = control.get('confirmEmail');

  if (email && confirmEmail && email.value !== confirmEmail.value) {
    return { emailMismatch: true };
  }

  return null;
};

@Component({
  selector: 'app-checkout-modal',
  standalone: true,
  imports: [
    MatDialogModule,
    StepperComponent,
    ButtonComponent,
    IconButtonComponent,
    ReactiveFormsModule,
    BraintreePaymentComponent,
    CurrencyPipe,
    LoadingSpinnerComponent,
    SignUpFormComponent,
    SignInFormComponent,
    ContactInfoFormComponent,
    BillingInfoFormComponent,
    ForgotPasswordFormComponent,
    MagicLinkFormComponent
  ],
  templateUrl: './checkout-modal.component.html',
  styleUrl: './checkout-modal.component.scss'
})
export class CheckoutModalComponent implements OnInit, OnDestroy {
  authContextService = inject(AuthContextService);
  authService = inject(AuthService);
  courseService = inject(CourseService);
  data = inject<CheckoutModalDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<CheckoutModalComponent>);
  private checkoutService = inject(CheckoutService);
  private destroyRef = inject(DestroyRef);
  private modalService = inject(BsModalService);
  private router = inject(Router);

  braintreePaymentComponent = viewChild<BraintreePaymentComponent>('braintreePayment');

  steps = signal([
    { active: true, label: 'Information', disabled: false },
    { active: false, label: 'Billing', disabled: true },
    { active: false, label: 'Payment', disabled: true },
    { active: false, label: 'Complete', disabled: true }
  ]);
  activeForm = signal<'sign-in' | 'sign-up' | 'forgot-password' | 'magic-link'>(
    'sign-up'
  );
  previousActiveStepIndex = signal(0);
  paymentComplete = signal(false);
  orderReview = signal<{
    fullName: string;
    address: string;
    cityStateZip: string;
    phone: string;
  }>(null);
  courseAlreadyPurchased = signal(false);

  activeStepIndex = computed(() => {
    const index = this.steps().findIndex((step) => step.active);
    return index;
  });
  activeStepIndex$ = toObservable(this.activeStepIndex);

  activeStepIndexSub: Subscription;
  testProgressSub: Subscription;

  // step 1 forms
  contactInfoForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required])
  });
  signInForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required])
  });
  signUpForm: FormGroup = new FormGroup(
    {
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      confirmEmail: new FormControl('', [Validators.required]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).+$/)
      ])
    },
    {
      validators: matchEmails
    }
  );
  forgotPasswordForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email])
  });
  magicLinkForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email])
  });

  // step 2 form
  billingInfoForm: FormGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    address: new FormControl('', [Validators.required]),
    address2: new FormControl(''),
    city: new FormControl('', [Validators.required]),
    state: new FormControl('', [Validators.required]),
    zip: new FormControl('', [
      Validators.required,
      Validators.minLength(5),
      Validators.pattern('^[0-9]{5}(?:-[0-9]{4})?$')
    ]), //'^[0-9]{5}(?:-[0-9]{4})?$'
    phone: new FormControl('', [Validators.required])
  });

  cartId = signal<string>(null);
  finalPrices = signal({
    subTotal: 0,
    tax: 0,
    total: 0
  });
  paymentValid = signal(false);
  loading = signal(false);
  errorState = signal<{
    stepOne: string;
    stepTwo: string;
    stepThree: string;
    stepFour: string;
  }>(null);
  showForgotPasswordInstructions = signal(false);
  showMagicLinkInstructions = signal(false);
  sportysCourseName = signal<string>('');
  isFircAcr = signal(false);

  stepOneError = computed(() => this.errorState()?.stepOne);
  stepTwoError = computed(() => this.errorState()?.stepTwo);
  stepThreeError = computed(() => this.errorState()?.stepThree);
  stepFourError = computed(() => this.errorState()?.stepFour);

  ngOnDestroy(): void {
    this.activeStepIndexSub?.unsubscribe();
    this.testProgressSub?.unsubscribe();
  }

  ngOnInit(): void {
    // Regardless if the course name starts with 'Sporty's ' or not, we want to have a coure name signal
    // that always starts with 'Sporty's '. This way, the user always sees 'Sporty's ' as part of the course
    // name without duplicating it.
    this.sportysCourseName.set(this.data.courseName);
    if (!this.data.courseName.startsWith(`Sporty's `)) {
      this.sportysCourseName.set(`Sporty's ${this.data.courseName}`);
    }
    this.isFircAcr.set(this.data.courseId === 'FIRC_ACR');

    this.contactInfoForm.statusChanges.subscribe((status: FormControlStatus) => {
      if (status === 'VALID') {
        this.enableStepIndex(1);

        if (this.billingInfoForm.valid) {
          this.enableStepIndex(2);
        }
      } else {
        this.disableStepIndex(1);
        this.disableStepIndex(2);
        this.disableStepIndex(3);
      }
    });

    this.billingInfoForm.statusChanges.subscribe((status: FormControlStatus) => {
      if (status === 'VALID' && !this.stepTwoError()) {
        this.enableStepIndex(2);
      } else if (status !== 'DISABLED') {
        this.disableStepIndex(2);
        this.disableStepIndex(3);
      }
    });

    if (this.authContextService.user) {
      this.contactInfoForm.patchValue({
        email: this.authContextService.user.email,
        firstName: this.authContextService.user.firstName,
        lastName: this.authContextService.user.lastName
      });
    }

    this.activeStepIndexSub = this.activeStepIndex$.subscribe((index) => {
      // if the user is on the billing step and the cart hasn't been created yet, create the cart
      if (index === 1 && !this.cartId()) {
        this.loading.set(true);
        this.createCart();
        // if the user leaves the billing step, save the billing info on the server
      } else if (index !== 1 && this.previousActiveStepIndex() === 1) {
        this.saveBillingAddress();
        // if the user is on the overview (complete) step, disable the billing info form
      } else if (index === 3) {
        this.billingInfoForm.disable();
        this.initOrderReview();
        // if the user is leaving the overview step, enable the billing info form
      } else if (index !== 3 && this.previousActiveStepIndex() === 3) {
        this.billingInfoForm.enable();
      }
    });
  }

  onClose(): void {
    if (this.paymentComplete()) {
      this.handleDialogClose();
      return;
    }

    const modal = this.modalService.show(ConfirmationModalComponent, {
      initialState: {
        message: 'Are you sure you would like to leave the checkout process?',
        warningMessage: null,
        yesLabel: 'Yes',
        noLabel: 'No',
        primaryButton: 'yes'
      },
      class: 'rounded-modal '
    });

    const sub = this.modalService.onHide.pipe(take(1)).subscribe((value) => {
      if (modal.content.response) {
        this.handleDialogClose();
      }
      modal.hide();
    });
  }

  onStepClicked(stepIndex: number): void {
    this.previousActiveStepIndex.set(this.activeStepIndex());
    this.errorState.set(null);

    this.steps.update((value) =>
      value.map((step, index) => ({
        ...step,
        active: index === stepIndex
      }))
    );
  }

  onPreviousClicked(): void {
    this.previousActiveStepIndex.set(this.activeStepIndex());
    this.errorState.set(null);

    this.steps.update((value) => {
      for (let i = 0; i < value.length; i++) {
        if (value[i].active) {
          value[i].active = false;
          value[i - 1].active = true;
          break;
        }
      }

      return [...value];
    });
  }

  onNextClicked(): void {
    this.previousActiveStepIndex.set(this.activeStepIndex());
    this.errorState.set(null);

    this.steps.update((value) => {
      for (let i = 0; i < value.length; i++) {
        if (value[i].active) {
          value[i].active = false;
          value[i + 1].active = true;
          break;
        }
      }

      return [...value];
    });
  }

  signUp(event) {
    this.loading.set(true);
    this.errorState.set(null);
    this.authService
      .register(
        this.signUpForm.get('firstName').value,
        this.signUpForm.get('lastName').value,
        this.signUpForm.get('email').value,
        this.signUpForm.get('password').value,
        {
          webAppId: this.data.courseId
        },
        null,
        null,
        null,
        true // isPurchaseProcess
      )
      .then(
        (value: any) => {
          this.loading.set(false);
          this.mapRespToContactInfoForm(value);
        },
        (reason) => {
          this.loading.set(false);
          this.errorState.update((value) => {
            return {
              ...value,
              stepOne: reason?.error?.message || 'Failed to sign up.'
            };
          });
        }
      );
  }

  signIn() {
    this.loading.set(true);
    this.errorState.set(null);
    this.authService
      .login(this.signInForm.get('email').value, this.signInForm.get('password').value)
      .pipe(
        catchError((error) => {
          this.loading.set(false);
          this.errorState.update((value) => {
            return {
              ...value,
              stepOne: error?.error?.message || 'Failed to sign in.'
            };
          });

          return EMPTY;
        })
      )
      .subscribe((value) => {
        this.loading.set(false);
        if (value.coursePermissions[this.data.courseId]) {
          this.courseAlreadyPurchased.set(true);
          this.paymentComplete.set(true);
          return;
        }
        this.mapRespToContactInfoForm(value);
      });
  }

  resetPassword() {
    this.loading.set(true);
    this.errorState.set(null);
    this.showForgotPasswordInstructions.set(false);
    this.authService.resetPassword(this.forgotPasswordForm.get('email').value).then(
      (value: any) => {
        this.loading.set(false);
        this.showForgotPasswordInstructions.set(true);
      },
      (error) => {
        this.loading.set(false);
        this.errorState.update((value) => ({
          ...value,
          stepOne: error?.error?.message
        }));
      }
    );
  }

  signInWithEmail() {
    this.loading.set(true);
    this.errorState.set(null);
    this.showMagicLinkInstructions.set(false);
    this.authService
      .sendMagicLink(this.magicLinkForm.get('email').value)
      .then((value) => {
        this.loading.set(false);
        this.showMagicLinkInstructions.set(true);
      })
      .catch((error) => {
        this.loading.set(false);
        this.errorState.update((value) => ({
          ...value,
          stepOne: error?.error?.title
        }));
      });
  }

  onPaymentValidityChange(event) {
    this.paymentValid.set(event);

    if (this.paymentValid() && this.contactInfoForm.valid && this.billingInfoForm.valid) {
      this.enableStepIndex(3);
    } else {
      this.disableStepIndex(3);
    }
  }

  onLoadingChange(event) {
    this.loading.set(event);
  }

  onPaymentComplete() {
    this.paymentComplete.set(true);
    this.disableStepIndex(0);
    this.disableStepIndex(1);
    this.disableStepIndex(2);
    if (!this.isFircAcr()) {
      this.authService.renew();
      this.testProgressSub = this.courseService
        .getTestProgress(this.data.courseId)
        .subscribe();
    }
  }

  onErrorCompletingPayment() {
    this.errorState.update((value) => {
      return {
        ...value,
        stepFour: 'Error completing payment. Please try again.'
      };
    });
  }

  completePayment() {
    this.loading.set(true);
    // call the completePayment method on the BraintreePaymentComponent directly
    // since that is where the dropInInstance from braintree is stored.
    this.braintreePaymentComponent().completePayment();
  }

  activateSignUpForm() {
    this.activeForm.set('sign-up');
    this.errorState.set(null);
  }

  activateSignInForm() {
    this.activeForm.set('sign-in');
    this.errorState.set(null);
  }

  activateForgotPasswordForm() {
    this.activeForm.set('forgot-password');
    this.errorState.set(null);
    this.showForgotPasswordInstructions.set(false);
  }

  activateMagicLinkForm() {
    this.activeForm.set('magic-link');
    this.errorState.set(null);
    this.showMagicLinkInstructions.set(false);
  }

  handleDialogClose(): void {
    this.dialogRef.close();
    if (!this.isFircAcr() && this.paymentComplete()) {
      this.router.navigate(['/course', this.data.courseId]);
    }
  }

  private createCart(): void {
    this.checkoutService
      .createCart(this.data.courseId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error: any) => {
          console.error('Error getting payment token:', error);
          localStorage.removeItem('paymentToken');
          this.loading.set(false);
          this.errorState.update((value) => {
            return {
              ...value,
              stepTwo:
                'Error creating cart for the current user. Please login and try again.'
            };
          });
          this.disableStepIndex(2);

          return EMPTY;
        })
      )
      .subscribe((response: CartInfo) => {
        this.loading.set(false);
        this.cartId.set(response.cartId);

        this.billingInfoForm.patchValue({
          firstName: response.billingAddress.firstName,
          lastName: response.billingAddress.lastName,
          address: response.billingAddress.street[0],
          address2: response.billingAddress.street[1] || '',
          city: response.billingAddress.city,
          state: response.billingAddress.regionCode,
          zip: response.billingAddress.postCode,
          phone: response.billingAddress.telephone
        });
      });
  }

  private saveBillingAddress(): void {
    const billingAddressDto = {
      city: this.billingInfoForm.get('city').value,
      countryId: IntStates.UNITED_STATES, // Hardcoding to US for now until we support international ordering (check billing-info-form.component.html)
      firstName: this.billingInfoForm.get('firstName').value,
      lastName: this.billingInfoForm.get('lastName').value,
      postCode: this.billingInfoForm.get('zip').value,
      regionCode: this.billingInfoForm.get('state').value,
      street: [
        this.billingInfoForm.get('address').value,
        this.billingInfoForm.get('address2').value || ''
      ],
      telephone: this.billingInfoForm.get('phone').value
    };

    this.checkoutService
      .saveBillingAddress(this.cartId(), billingAddressDto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: PricesResponse) => {
        console.log('Billing address saved:', response);
        this.finalPrices.set({
          subTotal: response.baseSubtotal,
          tax: response.baseTaxAmount,
          total: response.baseGrandTotal
        });
      });
  }

  private enableStepIndex(stepIndex: number): void {
    this.steps.update((value) => {
      value[stepIndex].disabled = false;
      return [...value];
    });
  }

  private disableStepIndex(stepIndex: number): void {
    this.steps.update((value) => {
      value[stepIndex].disabled = true;
      return [...value];
    });
  }

  private mapRespToContactInfoForm(value: any): void {
    if (!value) {
      return;
    }

    this.contactInfoForm.patchValue({
      email: value.email,
      firstName: value.firstName,
      lastName: value.lastName
    });
  }

  private initOrderReview(): void {
    this.orderReview.set({
      fullName: `${this.billingInfoForm.get('firstName').value} ${this.billingInfoForm.get('lastName').value
        }`,
      address: `${this.billingInfoForm.get('address').value} ${this.billingInfoForm.get('address2').value
        }`,
      cityStateZip: `${this.billingInfoForm.get('city').value} ${this.billingInfoForm.get('state').value
        } ${this.billingInfoForm.get('zip').value}`,
      phone: this.billingInfoForm.get('phone').value
    });
  }
}
